<template>
  <!--begin::Card-->
  <div class="card card-custom">
    <!--begin::Header-->
    <div class="card-header py-3">
      <div class="card-title flex-column align-items-start">
        <h3 class="card-label font-weight-bolder text-dark">
          Ready to Hire Talents
        </h3>
        <span v-if="meta" class="text-muted font-size-sm mt-1">
          {{ meta.total }} talents matched by AI
        </span>
      </div>
      <div class="card-toolbar">
        <span class="text-muted font-size-sm mr-4">
          Review → Contact → Get Interest → Nominate
        </span>
        <button
          type="button"
          class="btn btn-sm btn-light-info font-weight-bolder mr-2"
          @click="isFilterOpen = true"
        >
          <i class="fas fa-filter"></i>
          Filter
          <span v-if="filterCount" class="label label-sm label-info ml-1">
            {{ filterCount }}
          </span>
        </button>
        <button
          type="button"
          class="btn btn-sm btn-light-info font-weight-bolder"
          :disabled="loading"
          @click="fetchTalents"
        >
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
          Refresh
        </button>
      </div>
    </div>

    <div class="card-body">
      <!-- The tabs of the client's matching page, in their order -->
      <div v-if="meta?.roles?.length" class="mb-5">
        <button
          v-for="option in roleOptions"
          :key="option.id || 'all'"
          type="button"
          class="btn btn-sm font-weight-bolder rounded-pill mr-2 mb-2"
          :class="option.id === role ? 'btn-info' : 'btn-light-info'"
          @click="selectRole(option.id)"
        >
          {{ option.name }}
        </button>
      </div>
      <div v-if="filterCount" class="text-muted font-size-sm mb-5">
        <i class="fas fa-info-circle mr-1"></i>
        Filtered. Talents already being worked (contacted, interested, nominated
        or marked Not Relevant) stay in the list whatever the filters.
        <button
          type="button"
          class="btn btn-link btn-sm p-0 ml-1 font-weight-bold"
          @click="applyFilters(emptyFilters())"
        >
          Clear filters
        </button>
      </div>
      <div class="row">
        <div class="col-12">
          <div class="tab-content">
            <!--begin::Table-->
            <div v-if="rows.length" class="table-responsive mb-0">
              <table
                class="table table-head-custom table-vertical-center table-head-bg table-borderless"
              >
                <thead>
                  <tr class="text-left">
                    <th>Talent Name</th>
                    <th>Role</th>
                    <th>Qualified</th>
                    <th>Contact Status</th>
                    <th>WhatsApp</th>
                    <th>Relevance</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in rows"
                    :key="row.id"
                    :class="{ 'ready-to-hire__row--dimmed': isDimmed(row) }"
                  >
                    <td :title="row.full_name">
                      <a :href="`/dashboard/talent/${row.id}`" target="_blank">
                        {{ row.full_name }}
                      </a>
                    </td>
                    <td>{{ row.role?.name || "-" }}</td>
                    <td>{{ row.is_qualified ? "Yes" : "No" }}</td>
                    <td>
                      <span
                        class="ready-to-hire__pill"
                        :style="{
                          backgroundColor: contactStatus(row).bg,
                          color: contactStatus(row).color,
                        }"
                      >
                        {{ row.contact_status.name }}
                      </span>
                    </td>
                    <td>
                      <a
                        v-if="row.whatsapp"
                        class="ready-to-hire__whatsapp"
                        title="Open the conversation"
                        @click="openConversation(row)"
                      >
                        <span class="d-flex align-items-center text-nowrap">
                          <v-icon small :color="delivery(row).color">{{
                            delivery(row).icon
                          }}</v-icon>
                          <span
                            class="ml-1"
                            :style="{ color: delivery(row).color }"
                            >{{ row.whatsapp.status.name }}</span
                          >
                          <span
                            v-if="deliveryAt(row)"
                            class="text-muted ml-1"
                            :title="formatTime(deliveryAt(row))"
                          >
                            · {{ timeAgo(deliveryAt(row)) }}
                          </span>
                          <span
                            v-if="repliesCount(row)"
                            class="ready-to-hire__replies ml-2"
                            title="Messages from the talent"
                          >
                            <v-icon x-small color="white"
                              >mdi-message-text-outline</v-icon
                            >
                            {{ repliesCount(row) }}
                          </span>
                        </span>
                        <small
                          v-if="row.whatsapp.failure"
                          class="d-block text-muted ready-to-hire__hint"
                        >
                          {{ row.whatsapp.failure.name }}
                        </small>
                      </a>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td>
                      <div v-if="row.contact_status.id === 'not_relevant'">
                        <span
                          class="ready-to-hire__pill"
                          :style="{
                            backgroundColor: contactStatuses.not_relevant.bg,
                            color: contactStatuses.not_relevant.color,
                          }"
                        >
                          Not Relevant
                        </span>
                        <small
                          class="d-block text-muted mt-1 ready-to-hire__hint"
                        >
                          Reason: {{ notRelevantReason(row) }}
                        </small>
                        <button
                          v-if="canManage"
                          type="button"
                          class="btn btn-link btn-sm p-0 mt-1 font-weight-bold"
                          :disabled="!!busy[row.id]"
                          @click="undoNotRelevant(row)"
                        >
                          Undo
                        </button>
                      </div>
                      <!-- The theme gives a disabled .btn pointer-events: none, so its tooltip lives on a wrapper -->
                      <span
                        v-else-if="canManage"
                        v-b-tooltip.hover="
                          row.contact_status.id === 'nominated_to_client'
                            ? 'A talent nominated to the client cannot be marked not relevant'
                            : null
                        "
                        class="d-inline-block"
                      >
                        <button
                          type="button"
                          class="btn btn-link p-0 font-weight-bold ready-to-hire__mark"
                          :disabled="
                            !!busy[row.id] ||
                              row.contact_status.id === 'nominated_to_client'
                          "
                          @click="notRelevantRow = row"
                        >
                          Mark Not Relevant
                        </button>
                      </span>
                      <span v-else class="text-muted">—</span>
                    </td>
                    <td>
                      <button
                        v-if="['notify', 'resend'].includes(action(row))"
                        type="button"
                        class="btn btn-sm font-weight-bolder ready-to-hire__action"
                        :class="[
                          action(row) === 'notify'
                            ? 'ready-to-hire__action--solid'
                            : 'ready-to-hire__action--outline',
                          {
                            'spinner spinner-right': busy[row.id],
                            'spinner-light':
                              busy[row.id] && action(row) === 'notify',
                          },
                        ]"
                        :style="actionColors('whatsapp_sent')"
                        :disabled="!!busy[row.id]"
                        @click="notify(row)"
                      >
                        {{
                          action(row) === "notify"
                            ? "Notify via WhatsApp"
                            : "Resend via WhatsApp"
                        }}
                      </button>
                      <span
                        v-else-if="action(row) === 'awaiting'"
                        v-b-tooltip.hover="
                          row.can_resend_at
                            ? `You can resend in ${resendIn(row)}`
                            : null
                        "
                        class="d-inline-block"
                      >
                        <button
                          type="button"
                          class="btn btn-sm btn-light font-weight-bolder ready-to-hire__action"
                          disabled
                        >
                          Awaiting Response
                        </button>
                      </span>
                      <button
                        v-else-if="action(row) === 'nominate'"
                        type="button"
                        class="btn btn-sm font-weight-bolder ready-to-hire__action ready-to-hire__action--solid"
                        :class="{
                          'spinner spinner-light spinner-right': busy[row.id],
                        }"
                        :style="actionColors('nominated_to_am')"
                        :disabled="!!busy[row.id]"
                        @click="nominate(row)"
                      >
                        Nominate to AM
                      </button>
                      <button
                        v-else-if="action(row) === 'nominate-to-client'"
                        type="button"
                        class="btn btn-sm font-weight-bolder ready-to-hire__action ready-to-hire__action--solid"
                        :class="{
                          'spinner spinner-light spinner-right': busy[row.id],
                        }"
                        :style="actionColors('nominated_to_client')"
                        :disabled="!!busy[row.id]"
                        @click="openNominateToClient(row)"
                      >
                        Nominate to Client
                      </button>
                      <span
                        v-else-if="action(row) === 'awaiting-account-manager'"
                        v-b-tooltip.hover="
                          'Only the account manager can nominate to the client'
                        "
                        class="d-inline-block"
                      >
                        <button
                          type="button"
                          class="btn btn-sm font-weight-bolder ready-to-hire__action ready-to-hire__action--light"
                          :style="actionColors('nominated_to_client')"
                          disabled
                        >
                          Nominate to Client
                        </button>
                      </span>
                      <small
                        v-else-if="action(row) === 'update-phone'"
                        class="text-muted ready-to-hire__hint"
                      >
                        —, update the phone to resend
                      </small>
                      <span v-else class="text-muted">—</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div
              v-else-if="loading"
              class="d-flex justify-content-center align-items-center"
              style="min-height: 40rem"
            >
              <v-progress-circular indeterminate color="primary" />
            </div>
            <h5
              class="my-5 text-muted text-center d-flex justify-content-center align-items-center"
              style="min-height: 40rem"
              v-else
            >
              {{
                filterCount
                  ? "No talents match these filters"
                  : "No Matched Talents Yet"
              }}
            </h5>
            <div class="pagination-container">
              <p v-if="rows.length && meta" class="pagination-container__desc">
                {{
                  $t("showing_results", {
                    length: rows.length,
                    total: meta.total,
                  })
                }}
              </p>
              <b-pagination
                v-if="meta && meta.last_page > 1"
                v-model="page"
                :total-rows="meta.total"
                :per-page="perPage"
                align="center"
                last-number
                first-number
              ></b-pagination>
            </div>
            <!--end::Table-->
          </div>
        </div>
      </div>
    </div>

    <MarkNotRelevantModal
      v-if="notRelevantRow"
      :talentName="notRelevantRow.full_name"
      :reasons="notRelevantReasons"
      :loading="!!busy[notRelevantRow.id]"
      @confirm="markNotRelevant"
      @cancel="notRelevantRow = null"
    />

    <NominateTalentToClientModal
      v-if="nominee"
      :key="nominee.id"
      :talentId="nominee.id"
      :presetClient="presetClient"
      :contracts="[]"
      :currentRole="nominee.role || {}"
      @nominated="fetchTalents"
      @close="nominee = null"
    />

    <WhatsAppConversationDrawer
      v-model="isConversationOpen"
      :talentName="conversationRow?.full_name"
      :whatsapp="conversationRow?.whatsapp"
    />
    <ReadyToHireFilterDrawer
      v-model="isFilterOpen"
      :projectId="selectedProject"
      :role="role"
      :filters="filters"
      @apply="applyFilters"
    />
  </div>
</template>

<script>
import axios from "axios";
import dayjs from "dayjs";
import escape from "lodash/escape";
import Swal from "sweetalert2";
import { mapActions, mapGetters, mapMutations } from "vuex";
import toasted from "@/core/plugins/vue-toasted";
import {
  WHATSAPP_DELIVERY_STATUSES,
  WHATSAPP_FAILURE_STATUSES,
} from "@/constants/whatsapp-delivery";
import MarkNotRelevantModal from "@/components/client-profile/modals/MarkNotRelevantModal.vue";
import WhatsAppConversationDrawer from "@/components/client-profile/WhatsAppConversationDrawer.vue";
import ReadyToHireFilterDrawer from "@/components/client-profile/ReadyToHireFilterDrawer.vue";
import NominateTalentToClientModal from "@/components/talent-profile/modals/NominateTalentToClientModal.vue";
import {
  countReadyToHireFilters,
  emptyReadyToHireFilters,
  readyToHireFilterParams,
} from "@/constants/ready-to-hire-filters";

const CONTACT_STATUSES = {
  not_contacted: { bg: "#F1F2F4", color: "#6B7280" },
  whatsapp_sent: { bg: "#E7EEFE", color: "#2563EB" },
  talent_no_response: { bg: "#FDF3DA", color: "#B7791F" },
  talent_is_interested: { bg: "#E4F7EC", color: "#16A34A" },
  talent_not_interested: { bg: "#FCE9E9", color: "#DC2626" },
  nominated_to_am: { bg: "#EFEBFC", color: "#6C5CE7" },
  nominated_to_client: { bg: "#E0F7F6", color: "#0F8F88" },
  not_relevant: { bg: "#4B5563", color: "#FFFFFF" },
};

export default {
  name: "ReadyToHireTalents",
  components: {
    MarkNotRelevantModal,
    WhatsAppConversationDrawer,
    ReadyToHireFilterDrawer,
    NominateTalentToClientModal,
  },
  props: {
    selectedProject: {
      type: Number,
    },
  },
  data() {
    return {
      rows: [],
      meta: null,
      page: 1,
      perPage: 15,
      // The role id the list is narrowed to; null for every role
      role: null,
      // The filters of the client's matching page the list is narrowed by
      filters: emptyReadyToHireFilters(),
      isFilterOpen: false,
      loading: false,
      // Bumped per list request, so only the latest response fills the table.
      fetchSeq: 0,
      // Talent ids with a request in flight, so a row cannot be acted on twice.
      busy: {},
      contactStatuses: CONTACT_STATUSES,
      // The Not Relevant reasons ({ id, name }), from the API
      notRelevantReasons: [],
      notRelevantRow: null,
      nominee: null,
      conversationTalentId: null,
      isConversationOpen: false,
    };
  },
  computed: {
    ...mapGetters({
      clientProfile: "clients/getProfileData",
      projectData: "clients/getProfileDataProject",
    }),
    canManage() {
      return !!this.meta?.can_manage;
    },
    filterCount() {
      return countReadyToHireFilters(this.filters);
    },
    roleOptions() {
      return [{ id: null, name: "All" }, ...this.meta.roles];
    },
    conversationRow() {
      return this.rows.find((row) => row.id === this.conversationTalentId);
    },
    presetClient() {
      return {
        project_id: this.selectedProject,
        client_name: `${this.clientProfile.first_name} ${this.clientProfile.last_name}`,
        project_internal_name: this.projectData.internal_name,
      };
    },
  },
  mounted() {
    this.fetchTalents();
    this.getTalentNotRelevantReasons().then((res) => {
      this.notRelevantReasons = res.data.data;
    });
  },
  watch: {
    page() {
      this.fetchTalents();
    },
  },
  methods: {
    ...mapActions({
      getReadyToHireTalents: "clients/getReadyToHireTalents",
      notifyReadyToHireTalent: "clients/notifyReadyToHireTalent",
      markReadyToHireTalentNotRelevant:
        "clients/markReadyToHireTalentNotRelevant",
      undoReadyToHireTalentNotRelevant:
        "clients/undoReadyToHireTalentNotRelevant",
      nominateReadyToHireTalent: "clients/nominateReadyToHireTalent",
      getTalentNotRelevantReasons: "clients/getTalentNotRelevantReasons",
    }),
    ...mapMutations({
      setTalentProfile: "talentProfile/setProfileData",
    }),
    fetchTalents() {
      const seq = ++this.fetchSeq;
      this.loading = true;
      this.getReadyToHireTalents({
        project_id: this.selectedProject,
        page: this.page,
        perPage: this.perPage,
        role: this.role,
        filters: readyToHireFilterParams(this.filters),
      })
        .then((res) => {
          if (seq !== this.fetchSeq) return;

          this.rows = res.data.data;
          this.meta = res.data.meta;
        })
        // The axios interceptor already shows the error.
        .catch(() => {})
        .finally(() => {
          if (seq === this.fetchSeq) this.loading = false;
        });
    },
    selectRole(role) {
      if (role === this.role) return;

      this.role = role;
      // Skills and technical languages belong to the role left
      this.filters = {
        ...this.filters,
        skills: [],
        primary_technical_language: null,
        secondary_technical_language: null,
      };
      this.reload();
    },
    emptyFilters: emptyReadyToHireFilters,
    applyFilters(filters) {
      this.filters = filters;
      this.reload();
    },
    // From the first page
    reload() {
      // The page watcher fetches; on page 1 it would not fire
      if (this.page === 1) this.fetchTalents();
      else this.page = 1;
    },
    runAction(row, request) {
      this.$set(this.busy, row.id, true);

      return request
        .then((res) => {
          const updatedRow = res.data.data;
          const index = this.rows.findIndex(({ id }) => id === updatedRow.id);
          if (index !== -1) this.rows.splice(index, 1, updatedRow);

          return updatedRow;
        })
        .catch((err) => {
          // A 422 usually means the row changed elsewhere (e.g. a teammate acted first): reload it.
          if (err.response?.status === 422) this.fetchTalents();
          throw err;
        })
        .finally(() => {
          this.$delete(this.busy, row.id);
        });
    },
    async notify(row) {
      if (this.busy[row.id]) return;

      // The talent already answered no: make sure the recruiter means it
      if (row.contact_status.id === "talent_not_interested") {
        const { isConfirmed } = await Swal.fire({
          title: "Are you sure",
          text: `${row.full_name} is not interested. Resend the WhatsApp message anyway?`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#6f56c5",
          cancelButtonColor: "#E4E6EF",
          customClass: {
            cancelButton: "text-dark",
            icon: "text-danger border-danger",
          },
          confirmButtonText: "Yes, resend",
        });
        if (!isConfirmed) return;
      }

      const isResend = row.contact_status.id !== "not_contacted";
      const request = this.notifyReadyToHireTalent({
        project_id: this.selectedProject,
        talent_id: row.id,
      });

      this.runAction(row, request)
        .then((updatedRow) => {
          if (
            WHATSAPP_FAILURE_STATUSES.includes(updatedRow.whatsapp?.status?.id)
          ) {
            const reason = updatedRow.whatsapp.failure?.name;
            toasted.show(
              `WhatsApp message not delivered: ${
                updatedRow.whatsapp.status.name
              }${reason ? ` (${reason})` : ""}`,
              { type: "error", icon: "error" }
            );
            return;
          }

          toasted.show(
            isResend
              ? "WhatsApp message resent"
              : `WhatsApp message sent to ${escape(row.full_name)}`,
            { type: "success", icon: "check_circle" }
          );
        })
        // The axios interceptor already shows the error.
        .catch(() => {});
    },
    markNotRelevant({ reason, note }) {
      const row = this.notRelevantRow;
      if (this.busy[row.id]) return;

      const request = this.markReadyToHireTalentNotRelevant({
        project_id: this.selectedProject,
        talent_id: row.id,
        reason,
        note,
      });

      this.runAction(row, request)
        .then(() => {
          this.notRelevantRow = null;
          toasted.show("Talent marked as Not Relevant", {
            type: "success",
            icon: "check_circle",
          });
        })
        // The axios interceptor already shows the error. The form is validated client-side,
        // so a 422 is a state rejection: close the modal over the reloaded row.
        .catch((err) => {
          if (err.response?.status === 422) this.notRelevantRow = null;
        });
    },
    undoNotRelevant(row) {
      if (this.busy[row.id]) return;

      const request = this.undoReadyToHireTalentNotRelevant({
        project_id: this.selectedProject,
        talent_id: row.id,
      });

      this.runAction(row, request)
        .then(() => {
          toasted.show(`${escape(row.full_name)} is no longer Not Relevant`, {
            type: "success",
            icon: "check_circle",
          });
        })
        // The axios interceptor already shows the error.
        .catch(() => {});
    },
    nominate(row) {
      if (this.busy[row.id]) return;

      const request = this.nominateReadyToHireTalent({
        project_id: this.selectedProject,
        talent_id: row.id,
      });

      this.runAction(row, request)
        .then(() => {
          toasted.show(
            `${escape(row.full_name)} nominated to AM`,
            { type: "success", icon: "check_circle" }
          );
        })
        // The axios interceptor already shows the error.
        .catch(() => {});
    },
    // The nomination popup reads the talent from the talentProfile store, so load it first.
    // Not through talentProfile/getProfileData: that action never settles when the request fails.
    openNominateToClient(row) {
      if (this.busy[row.id]) return;

      this.$set(this.busy, row.id, true);
      axios
        .get(`users/${row.id}`)
        .then((res) => {
          this.setTalentProfile(res.data.data);
          this.nominee = row;
        })
        // The axios interceptor already shows the error.
        .catch(() => {})
        .finally(() => {
          this.$delete(this.busy, row.id);
        });
    },
    openConversation(row) {
      this.conversationTalentId = row.id;
      this.isConversationOpen = true;
    },
    // Which control the Action cell shows; Notify/Resend availability comes from the backend.
    action(row) {
      const status = row.contact_status.id;

      if (status === "nominated_to_am") {
        return row.can_nominate_to_client
          ? "nominate-to-client"
          : "awaiting-account-manager";
      }
      if (status === "talent_is_interested") {
        return this.canManage ? "nominate" : "none";
      }
      if (
        ![
          "not_contacted",
          "whatsapp_sent",
          "talent_no_response",
          "talent_not_interested",
        ].includes(status)
      ) {
        return "none";
      }
      if (this.canManage && row.can_notify) {
        return status === "not_contacted" ? "notify" : "resend";
      }
      if (status === "whatsapp_sent") {
        const delivery = row.whatsapp?.status?.id;
        if (delivery === "not_on_whatsapp") {
          return this.canManage ? "update-phone" : "none";
        }
        if (!WHATSAPP_FAILURE_STATUSES.includes(delivery)) {
          return "awaiting";
        }
      }

      return "none";
    },
    isDimmed(row) {
      return ["talent_not_interested", "not_relevant"].includes(
        row.contact_status.id
      );
    },
    contactStatus(row) {
      return (
        CONTACT_STATUSES[row.contact_status.id] || {
          bg: "#F1F2F4",
          color: "#6B7280",
        }
      );
    },
    // An action wears the colours of the Contact Status it leads to
    actionColors(statusId) {
      const { bg, color } = CONTACT_STATUSES[statusId];
      return { "--action-bg": bg, "--action-color": color };
    },
    delivery(row) {
      return WHATSAPP_DELIVERY_STATUSES[row.whatsapp.status.id] || {};
    },
    deliveryAt(row) {
      const { status, sent_at, delivered_at, read_at } = row.whatsapp;

      return { read: read_at, delivered: delivered_at }[status.id] || sent_at;
    },
    repliesCount(row) {
      return row.whatsapp.messages.filter((message) => !message.is_outgoing)
        .length;
    },
    notRelevantReason(row) {
      if (row.not_relevant_reason?.id === "other" && row.not_relevant_note) {
        return row.not_relevant_note;
      }

      return row.not_relevant_reason?.name || "-";
    },
    timeAgo(date) {
      const minutes = dayjs().diff(dayjs(date), "minute");
      if (minutes < 1) return "now";
      if (minutes < 60) return `${minutes}m`;

      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h`;

      return `${Math.floor(hours / 24)}d`;
    },
    resendIn(row) {
      const minutes = dayjs(row.can_resend_at).diff(dayjs(), "minute");
      if (minutes < 60) return `${Math.max(minutes, 1)}m`;

      return `${Math.ceil(minutes / 60)}h`;
    },
    formatTime(date) {
      return dayjs(date).format("DD/MM/YYYY hh:mm a");
    },
  },
};
</script>

<style lang="scss" scoped>
.ready-to-hire__row--dimmed {
  opacity: 0.55;
}

.ready-to-hire__pill {
  display: inline-block;
  white-space: nowrap;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.ready-to-hire__whatsapp {
  cursor: pointer;
}

.ready-to-hire__replies {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 6px;
  border-radius: 10px;
  background-color: #16a34a;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}

.ready-to-hire__hint {
  display: block;
  max-width: 200px;
  white-space: normal;
}

.ready-to-hire__mark {
  color: #dc5a5a;
  white-space: nowrap;
}

.ready-to-hire__action {
  max-width: 150px;
  white-space: normal;
  line-height: 1.3;
}

.btn.ready-to-hire__action--solid,
.btn.ready-to-hire__action--outline:hover:not(:disabled) {
  background-color: var(--action-color);
  border-color: var(--action-color);
  color: #fff;
}

.btn.ready-to-hire__action--solid:hover:not(:disabled) {
  color: #fff;
  filter: brightness(0.9);
}

.btn.ready-to-hire__action--outline {
  background-color: transparent;
  border-color: var(--action-color);
  color: var(--action-color);
}

.btn.ready-to-hire__action--light {
  background-color: var(--action-bg);
  border-color: var(--action-bg);
  color: var(--action-color);
}
</style>
